import { AttributeLookupInput } from "#batteries-included-components/Forms/FormSubmissionForm/FormSubmissionSections/FormSubmissionField/QuestionField/LookupField/AttributeLookupInput";
import { FormSchemaDropdownInput } from "#batteries-included-components/Forms/FormSubmissionForm/FormSubmissionSections/FormSubmissionField/QuestionField/LookupField/FormSchemaDropdownInput";
import { CustomAttributeKeyValueList } from "#src/batteries-included-components/KeyValueList/CustomAttributeKeyValueList";
import { useFormContext } from "@validereinc/common-components";
import { AssetTypeType, ResourceLookupType } from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import { get } from "es-toolkit/compat";
import React, { useMemo } from "react";
import { DeviceFormSubmissionDropdownInput } from "./DeviceFormSubmissionDropdownInput";
import { EquipmentFormSubmissionDropdownInput } from "./EquipmentFormSubmissionDropdownInput";
import { FacilityFormSubmissionDropdownInput } from "./FacilityFormSubmissionDropdownInput";
import { FlowFormSubmissionDropdownInput } from "./FlowFormSubmissionDropdownInput";
import { resolveLookupFilterPlaceholders } from "./LookupField.helpers";
import styles from "./LookupField.module.scss";

const { getParentQuestionName, getValuePathFromQuestionPath } = Forms.logic;
const cx = classNames.bind(styles);

export const LookupField = ({
  lookup_entity_type,
  lookup_entity_attribute,
  parent_facility_question_id,
  name,
  filter,
  lookup_entity_display_attributes = [],
  questionId,
  isMulti = false,
  ...rest
}: {
  lookup_entity_display_attributes?: string[];
  lookup_entity_type?: ResourceLookupType;
  lookup_entity_attribute?: string;
  parent_facility_question_id?: string;
  name: string;
  questionId?: string;
  filter?: Record<string, any>;
  label?: string;
  isRequired?: boolean;
  isFluid?: boolean;
  isMulti?: boolean;
}) => {
  const { watch, getValues } = useFormContext();
  const parentFacilityQuestionName = useMemo(() => {
    if (parent_facility_question_id) {
      return getParentQuestionName({
        currentQuestionId: name,
        formValues: getValues(),
        questionIdToFind: parent_facility_question_id,
      });
    }
    return "";
  }, [parent_facility_question_id]);

  const parentFacilityId = parentFacilityQuestionName
    ? watch(getValuePathFromQuestionPath(parentFacilityQuestionName))
    : null;

  const values = watch();

  const filterObject = useMemo(() => {
    const values = getValues();

    if (!filter && !parentFacilityQuestionName) {
      return null;
    }

    const resolvePlaceholder = (referencedQuestionId: string): unknown => {
      const questionName = getParentQuestionName({
        currentQuestionId: name,
        formValues: values,
        questionIdToFind: referencedQuestionId,
      });

      return get(values, getValuePathFromQuestionPath(questionName));
    };

    const formattedFilter = {
      ...(parentFacilityQuestionName
        ? { "facility.id": parentFacilityId }
        : {}),
      ...(resolveLookupFilterPlaceholders(
        filter ?? {},
        resolvePlaceholder
      ) as Record<string, unknown>),
    };

    return Object.keys(formattedFilter).length ? formattedFilter : undefined;
  }, [filter, values, parentFacilityId, parentFacilityQuestionName]);

  const props: any = {
    ...rest,
    name,
    filter: filterObject,
    isMulti,
  };

  if (lookup_entity_attribute) {
    return (
      <AttributeLookupInput
        lookup_entity_attribute={lookup_entity_attribute}
        lookup_entity_type={lookup_entity_type as AssetTypeType}
        {...props}
      />
    );
  }
  return (
    <>
      {lookup_entity_type === "facility" ? (
        <FacilityFormSubmissionDropdownInput {...props} />
      ) : null}

      {lookup_entity_type === "equipment" ? (
        <EquipmentFormSubmissionDropdownInput {...props} />
      ) : null}

      {lookup_entity_type === "flow" ? (
        <FlowFormSubmissionDropdownInput {...props} />
      ) : null}

      {lookup_entity_type === "device" ? (
        <DeviceFormSubmissionDropdownInput {...props} />
      ) : null}

      {lookup_entity_type === "form_schema" ? (
        <FormSchemaDropdownInput {...props} />
      ) : null}

      <div className={cx("attributeContainer")}>
        <CustomAttributeKeyValueList
          assetType={lookup_entity_type as AssetTypeType}
          assetId={getValues(name)}
          displayAttributes={lookup_entity_display_attributes}
        />
      </div>
    </>
  );
};
